import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../MosqifyAuthContext';
import { IconChevronLeft, IconEye, IconEyeSlash } from '../../shared/Icons';
// neonAuthService is NOT used for Google OAuth - Google always uses API service via GSI
// import neonAuthService from '../../services/neonAuthService';
import config from '../../config/appConfig';
import Toast from '../shared/Toast';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import {
  validateEmail,
  validateName,
  validatePhoneNumber,
  validatePasswordConfirmation,
  formatPhoneNumber,
  checkPasswordStrength,
} from '../../utils/validation';
import { captureEvent } from '../../posthogClient';

const MosqifyApiLoginScreen = () => {
  const { login, register, loginWithGoogle, isLoading, user, isAuthenticated, resendVerificationEmail } = useAuth();
  const navigate = useNavigate();
  const googleButtonRef = useRef(null);
  
  // Redirect jika user sudah login
  useEffect(() => {
    if (isAuthenticated && user) {
      const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/beranda';
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);
  
  // State declarations - must be before useEffect hooks that use them
  const [showForm, setShowForm] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
  });
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'info' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  // Prevent scroll pada body dan html saat di login screen
  useEffect(() => {
    // Disable scroll
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.height = '100vh';
    document.documentElement.style.height = '100vh';
    
    // Cleanup: Enable scroll kembali saat component unmount
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.height = '';
    };
  }, []);

  // Handle Google Sign-In - always use Google Identity Services (GSI)
  // Google OAuth always uses API service, not Neon Auth
  // Neon Auth is only used for email/password authentication
  // NOTE: This function is not used anymore - GSI button handles everything directly
  const handleGoogleSignIn = useCallback(() => {
    // This function is kept for backward compatibility but should not be called
    // The Google Identity Services button handles OAuth directly via its callback
    console.warn('[Google Sign-In] handleGoogleSignIn called but GSI button should handle OAuth directly');
    setError('Silakan gunakan tombol Google Sign-In yang tersedia.');
  }, []);

  // Show toast notification
  const showToast = (message, type = 'info', duration = 3000) => {
    setToast({ isVisible: true, message, type });
    setTimeout(() => {
      setToast({ isVisible: false, message: '', type: 'info' });
    }, duration);
  };

  // Initialize Google Sign-In button
  // Always use Google Identity Services (GSI) for Google OAuth
  // Google OAuth always uses API service, not Neon Auth
  const initializeGoogleSignIn = useCallback(() => {
    console.log('[Google Auth] initializeGoogleSignIn called', {
      hasGoogle: !!window.google?.accounts,
      hasButtonRef: !!googleButtonRef.current
    });

    if (!window.google?.accounts) {
      console.warn('[Google Auth] Google Identity Services not available yet');
      return;
    }

    if (!googleButtonRef.current) {
      console.warn('[Google Auth] Button ref not available');
      return;
    }

    // Try multiple ways to get client ID - fallback to hardcoded for development
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 
                     '122655449090-jl874at3trgb5u4jcvudvtkghnv77kb5.apps.googleusercontent.com';
    
    // Check if client ID is available
    if (!clientId || clientId === '') {
      console.error('[Google Auth] Google Client ID is not set. Please check REACT_APP_GOOGLE_CLIENT_ID in .env file');
      setError('Google Sign-In tidak dapat dimuat. Silakan hubungi administrator.');
      return;
    }

    try {
      console.log('[Google Auth] Initializing Google Identity Services with client ID:', clientId.substring(0, 20) + '...');
      
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            console.log('[Google Auth] Google OAuth callback received');
            
            if (!response || !response.credential) {
              console.error('[Google Auth] Invalid Google response:', response);
              throw new Error('Invalid Google response. Silakan coba lagi.');
            }

            console.log('[Google Auth] Calling loginWithGoogle with idToken');
            // This will use API service, NOT Neon Auth
            await loginWithGoogle(response.credential);
            
            console.log('[Google Auth] Login successful, redirecting...');
            showToast('Login berhasil!', 'success');
            const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/beranda';
            sessionStorage.removeItem('redirectAfterLogin');
            navigate(redirectPath, { replace: true });
          } catch (error) {
            console.error('[Google Auth] Error in callback:', error);
            const errorMsg = error.message || 'Google login gagal. Silakan coba lagi.';
            setError(errorMsg);
            showToast(errorMsg, 'error');
          }
        },
      });

      // Clear existing button before rendering new one
      if (googleButtonRef.current) {
        googleButtonRef.current.innerHTML = '';
      }

      // Render button dengan text sesuai mode (login atau signup)
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        text: isLoginMode ? 'signin_with' : 'signup_with',
        width: '100%',
        locale: 'id',
      });
      
      console.log('[Google Auth] Button rendered successfully in container');
    } catch (error) {
      console.error('[Google Auth] Failed to initialize button:', error);
      setError('Gagal memuat tombol Google Sign-In. Silakan refresh halaman.');
    }
  }, [isLoginMode, loginWithGoogle, navigate]);

  // Load Google Identity Services
  // Always load GSI for Google OAuth (uses API service, not Neon Auth)
  useEffect(() => {
    console.log('[Google Auth] Loading Google Identity Services script...');
    
    // Check if client ID is available - use fallback if not in env
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 
                     '122655449090-jl874at3trgb5u4jcvudvtkghnv77kb5.apps.googleusercontent.com';
    
    if (!clientId || clientId === '') {
      console.error('[Google Auth] REACT_APP_GOOGLE_CLIENT_ID is not set. Google Sign-In will not work.');
      console.error('[Google Auth] Make sure .env file exists in mosqify-pwa root with: REACT_APP_GOOGLE_CLIENT_ID=your-client-id');
      // Don't return - still try to load script with fallback
    } else {
      console.log('[Google Auth] Client ID found:', clientId.substring(0, 20) + '...');
    }

    // Check if script already loaded
    if (window.google?.accounts) {
      console.log('[Google Auth] Google Identity Services already loaded');
      setGoogleLoaded(true);
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('[Google Auth] Google Identity Services script loaded successfully');
      console.log('[Google Auth] window.google.accounts available:', !!window.google?.accounts);
      setGoogleLoaded(true);
    };
    script.onerror = (error) => {
      console.error('[Google Auth] Failed to load Google Identity Services script:', error);
      setError('Gagal memuat Google Sign-In. Silakan refresh halaman.');
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup script if component unmounts
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Initialize Google button when form is shown, Google is loaded, or mode changes
  useEffect(() => {
    console.log('[Google Auth] Button initialization check:', {
      showForm,
      googleLoaded,
      hasButtonRef: !!googleButtonRef.current,
      googleAvailable: !!window.google?.accounts
    });

    if (showForm && googleLoaded && googleButtonRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        console.log('[Google Auth] Initializing Google button...');
        initializeGoogleSignIn();
      }, 200);
      
      return () => clearTimeout(timer);
    } else if (showForm && !googleLoaded) {
      console.warn('[Google Auth] Form shown but Google script not loaded yet');
    } else if (showForm && !googleButtonRef.current) {
      console.warn('[Google Auth] Form shown but button ref not available');
    }
  }, [showForm, googleLoaded, isLoginMode, initializeGoogleSignIn]);

  // Real-time validation
  const validateField = (name, value) => {
    let validation = { isValid: true, error: '' };

    switch (name) {
      case 'email':
        validation = validateEmail(value);
        break;
      case 'name':
        if (!isLoginMode) {
          validation = validateName(value);
        }
        break;
      case 'phone':
        if (!isLoginMode) {
          validation = validatePhoneNumber(value);
        }
        break;
      case 'password':
        // Password validation is handled by strength indicator
        if (value && value.length < 6) {
          validation = { isValid: false, error: 'Password minimal 6 karakter' };
        }
        break;
      case 'confirmPassword':
        if (!isLoginMode && formData.password) {
          validation = validatePasswordConfirmation(formData.password, value);
        }
        break;
      default:
        break;
    }

    setFieldErrors(prev => ({
      ...prev,
      [name]: validation.error || '',
    }));

    return validation.isValid;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Format phone number if needed
    let processedValue = value;
    if (name === 'phone') {
      processedValue = formatPhoneNumber(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Real-time validation
    validateField(name, processedValue);

    // Clear general errors when user types
    setError('');
    setSuccess('');
    setNetworkError(false);
  };

  // Validate all fields before submission
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Validate email
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.error;
      isValid = false;
    }

    // Validate password
    if (!formData.password || formData.password.length < 6) {
      errors.password = 'Password minimal 6 karakter';
      isValid = false;
    }

    // Additional validation for registration
    if (!isLoginMode) {
      // Validate name
      const nameValidation = validateName(formData.name);
      if (!nameValidation.isValid) {
        errors.name = nameValidation.error;
        isValid = false;
      }

      // Validate password confirmation
      if (formData.password) {
        const confirmValidation = validatePasswordConfirmation(formData.password, formData.confirmPassword);
        if (!confirmValidation.isValid) {
          errors.confirmPassword = confirmValidation.error;
          isValid = false;
        }
      }

      // Check password strength for registration
      const passwordStrength = checkPasswordStrength(formData.password);
      if (passwordStrength.strength === 'weak' && formData.password.length > 0) {
        errors.password = 'Password terlalu lemah. Gunakan kombinasi huruf, angka, dan karakter khusus.';
        isValid = false;
      }

      // Validate phone if provided
      if (formData.phone) {
        const phoneValidation = validatePhoneNumber(formData.phone);
        if (!phoneValidation.isValid) {
          errors.phone = phoneValidation.error;
          isValid = false;
        }
      }

      // Check terms acceptance
      if (!acceptedTerms) {
        setError('Anda harus menyetujui Ketentuan Layanan dan Kebijakan Privasi');
        isValid = false;
      }
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNetworkError(false);
    setIsSubmitting(true);

    // Validate form
    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Check network connectivity
      if (!navigator.onLine) {
        setNetworkError(true);
        setError('Tidak ada koneksi internet. Silakan periksa koneksi Anda.');
        setIsSubmitting(false);
        return;
      }

      if (isLoginMode) {
        await login(formData.email, formData.password);
        showToast('Login berhasil!', 'success');
        // Wait a bit to ensure state is updated
        await new Promise(resolve => setTimeout(resolve, 500));
        // Redirect ke halaman sebelumnya jika ada, atau ke beranda
        const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/beranda';
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath, { replace: true });
      } else {
        // Remove confirmPassword from registration data
        const { confirmPassword, ...registrationData } = formData;
        const result = await register(registrationData);
        
        // Check if email verification is required
        if (result && result.requiresVerification) {
          // Show success toast
          showToast(`Email verifikasi telah dikirim ke ${result.email}`, 'success', 4000);
          setSuccess(`Email verifikasi telah dikirim ke ${result.email}. Silakan cek inbox Anda.`);
          setError(''); // Clear any errors
          
          // Wait a moment to show success message, then redirect
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Redirect to email verification screen
          navigate(`/verify-email?email=${encodeURIComponent(result.email)}&signup=success`, { replace: true });
          return;
        }
        
        // If no verification required (shouldn't happen with current config), proceed normally
        showToast('Registrasi berhasil!', 'success');
        setSuccess('Registrasi berhasil! Mengarahkan ke beranda...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        navigate('/beranda', { replace: true });
      }
    } catch (error) {
      captureEvent('login_failed', {
        mode: isLoginMode ? 'login' : 'register',
        errorCode: error.code,
        errorMessage: error.message?.slice(0, 100),
      });
      // Handle network errors
      if (error.isNetworkError || error.name === 'NetworkError' || (error.message && (error.message.includes('network') || error.message.includes('fetch') || error.message.includes('NetworkError')))) {
        setNetworkError(true);
        const errorMsg = error.message || 'Gagal terhubung ke server. Silakan periksa koneksi internet Anda dan coba lagi.';
        setError(errorMsg);
        showToast(errorMsg, 'error');
      }
      // Check if error is about unverified email
      else if (error.code === 'EMAIL_NOT_VERIFIED' || (error.message && (error.message.includes('belum diverifikasi') || error.message.includes('not verified') || error.message.includes('verifikasi')))) {
        // Show error with option to resend verification
        setError(error.message || 'Email Anda belum diverifikasi. Silakan cek inbox dan klik link verifikasi.');
        showToast('Email belum diverifikasi', 'warning');
        // Store email for resend functionality
        sessionStorage.setItem('unverifiedEmail', formData.email);
      } else {
        const errorMessage = error.message || 'Terjadi kesalahan. Silakan coba lagi.';
        setError(errorMessage);
        showToast(errorMessage, 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAccount = () => {
    setIsLoginMode(false);
    setShowForm(true);
  };

  const handleGuestLogin = async () => {
    // Guest login - langsung navigate tanpa auth
    navigate('/beranda');
  };

  const handleShowLogin = () => {
    setIsLoginMode(true);
    setShowForm(true);
  };

  const handleSkip = () => {
    // Skip login - navigate langsung
    navigate('/beranda');
  };

  // Mosqify Logo - Menggunakan file logo dari assets (Responsive & Larger)
  const MosqifyLogo = () => {
    return (
      <img 
        src="/Logo.jpg" 
        alt="Mosqify Logo" 
        className="w-80 h-80 sm:w-96 sm:h-96 md:w-[450px] md:h-[450px] max-w-[450px] mx-auto object-contain"
      />
    );
  };

  // Show form if user clicks login/register
  if (showForm) {
    return (
      <div 
        className="h-screen w-screen flex flex-col overflow-hidden bg-gray-100 dark:bg-gray-900" 
        style={{ overflow: 'hidden', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      >
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 flex items-center justify-between px-4 py-4 shadow-sm">
          <button 
            onClick={() => setShowForm(false)}
            className="p-2 -ml-2 text-gray-900 dark:text-gray-100"
          >
            <IconChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex-grow text-center pr-10">
            {isLoginMode ? 'Masuk' : 'Daftar'}
          </h1>
          <div className="w-10"></div> {/* Spacer untuk balance */}
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto px-4 pt-6 pb-8">
          <div className="max-w-md mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Register fields */}
              {!isLoginMode && (
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Nama Lengkap
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required={!isLoginMode}
                    aria-invalid={!!fieldErrors.name}
                    aria-describedby={fieldErrors.name ? 'name-error' : undefined}
                    className={`w-full px-4 py-3 bg-white dark:bg-gray-800 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm text-gray-900 dark:text-gray-100 ${
                      fieldErrors.name ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : 'border-gray-200 dark:border-gray-600'
                    }`}
                    placeholder="Masukkan nama lengkap"
                  />
                  {fieldErrors.name && (
                    <p id="name-error" className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                      {fieldErrors.name}
                    </p>
                  )}
                </div>
              )}

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  autoComplete="email"
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                  className={`w-full px-4 py-3 bg-white dark:bg-gray-800 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm text-gray-900 dark:text-gray-100 ${
                    fieldErrors.email ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : 'border-gray-200 dark:border-gray-600'
                  }`}
                  placeholder="Masukkan email"
                />
                {fieldErrors.email && (
                  <p id="email-error" className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              {/* Register phone field */}
              {!isLoginMode && (
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Nomor Telepon <span className="text-gray-400 text-xs">(Opsional)</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    autoComplete="tel"
                    aria-invalid={!!fieldErrors.phone}
                    aria-describedby={fieldErrors.phone ? 'phone-error' : undefined}
                    className={`w-full px-4 py-3 bg-white dark:bg-gray-800 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm text-gray-900 dark:text-gray-100 ${
                      fieldErrors.phone ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : 'border-gray-200 dark:border-gray-600'
                    }`}
                    placeholder="08xxxxxxxxxx"
                  />
                  {fieldErrors.phone && (
                    <p id="phone-error" className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                      {fieldErrors.phone}
                    </p>
                  )}
                </div>
              )}

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    autoComplete={isLoginMode ? "current-password" : "new-password"}
                    aria-invalid={!!fieldErrors.password}
                    aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                    className={`w-full px-4 py-3 pr-12 bg-white dark:bg-gray-800 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm text-gray-900 dark:text-gray-100 ${
                      fieldErrors.password ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : 'border-gray-200 dark:border-gray-600'
                    }`}
                    placeholder="Masukkan password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                    aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                  >
                    {showPassword ? (
                      <IconEyeSlash className="w-5 h-5" />
                    ) : (
                      <IconEye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p id="password-error" className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                    {fieldErrors.password}
                  </p>
                )}
                {/* Password Strength Indicator - only for registration */}
                {!isLoginMode && formData.password && (
                  <PasswordStrengthIndicator password={formData.password} />
                )}
              </div>

              {/* Password Confirmation Field - only for registration */}
              {!isLoginMode && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Konfirmasi Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required={!isLoginMode}
                      autoComplete="new-password"
                      aria-invalid={!!fieldErrors.confirmPassword}
                      aria-describedby={fieldErrors.confirmPassword ? 'confirmPassword-error' : undefined}
                      className={`w-full px-4 py-3 pr-12 bg-white dark:bg-gray-800 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm text-gray-900 dark:text-gray-100 ${
                        fieldErrors.confirmPassword ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : 'border-gray-200 dark:border-gray-600'
                      }`}
                      placeholder="Masukkan ulang password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                      aria-label={showConfirmPassword ? "Sembunyikan konfirmasi password" : "Tampilkan konfirmasi password"}
                    >
                      {showConfirmPassword ? (
                        <IconEyeSlash className="w-5 h-5" />
                      ) : (
                        <IconEye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {fieldErrors.confirmPassword && (
                    <p id="confirmPassword-error" className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                      {fieldErrors.confirmPassword}
                    </p>
                  )}
                </div>
              )}

              {/* Forgot Password Link - hanya untuk login mode */}
              {isLoginMode && (
                <div className="text-left">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-green-600 hover:text-green-700 text-sm font-medium"
                  >
                    Lupa kata sandi?
                  </button>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl p-3">
                  <p className="text-green-700 dark:text-green-300 text-sm">{success}</p>
                </div>
              )}

              {/* Network Error Message */}
              {networkError && (
                <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-xl p-3">
                  <p className="text-yellow-800 dark:text-yellow-200 text-sm mb-2">
                    <strong>Masalah Koneksi:</strong> {error}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setNetworkError(false);
                      setError('');
                      handleSubmit({ preventDefault: () => {} });
                    }}
                    className="text-yellow-800 hover:text-yellow-900 text-sm font-medium underline"
                  >
                    Coba Lagi
                  </button>
                </div>
              )}

              {/* Error Message */}
              {error && !networkError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-3" role="alert">
                  <p className="text-red-600 dark:text-red-300 text-sm mb-2">{error}</p>
                  {/* Show resend verification button if email not verified */}
                  {error.includes('belum diverifikasi') && (
                    <div className="mt-3 space-y-2">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const emailToResend = sessionStorage.getItem('unverifiedEmail') || formData.email;
                            await resendVerificationEmail(emailToResend);
                            setError('');
                            setSuccess('Email verifikasi telah dikirim ulang. Silakan cek inbox Anda.');
                            showToast('Email verifikasi telah dikirim ulang', 'success');
                            setTimeout(() => {
                              navigate(`/verify-email?email=${encodeURIComponent(emailToResend)}`);
                            }, 2000);
                          } catch (resendError) {
                            setError(resendError.message || 'Gagal mengirim ulang email verifikasi.');
                            showToast('Gagal mengirim ulang email verifikasi', 'error');
                          }
                        }}
                        className="text-green-600 hover:text-green-700 text-sm font-medium underline"
                      >
                        Kirim ulang email verifikasi
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`)}
                        className="block text-green-600 hover:text-green-700 text-sm font-medium underline"
                      >
                        Atau pergi ke halaman verifikasi
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Google Sign-In Button */}
              <div className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">atau</span>
                  </div>
                </div>
                
                {/* Google OAuth Button - Always use Google Identity Services (GSI) */}
                {/* Google OAuth always uses API service, not Neon Auth */}
                <div ref={googleButtonRef} className="w-full flex justify-center"></div>
              </div>

              {/* Terms Acceptance Checkbox - only for registration */}
              {!isLoginMode && (
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={acceptedTerms}
                    onChange={(e) => {
                      setAcceptedTerms(e.target.checked);
                      if (e.target.checked) {
                        setError('');
                      }
                    }}
                    className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    aria-describedby="terms-description"
                  />
                  <label htmlFor="acceptTerms" className="text-xs text-gray-600 dark:text-gray-400 flex-1">
                    <span id="terms-description">
                      Saya menyetujui{' '}
                      <a
                        href="/terms-of-service"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/terms-of-service');
                        }}
                        className="text-green-600 hover:text-green-700 font-medium underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ketentuan Layanan
                      </a>
                      {' '}dan{' '}
                      <a
                        href="/privacy-policy"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/privacy-policy');
                        }}
                        className="text-green-600 hover:text-green-700 font-medium underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Kebijakan Privasi
                      </a>
                      {' '}kami
                    </span>
                  </label>
                </div>
              )}

              {/* Legal Disclaimer - for login mode */}
              {isLoginMode && (
                <div className="text-xs text-gray-600 dark:text-gray-400 text-center px-4">
                  Dengan melanjutkan, Anda menyetujui{' '}
                  <a
                    href="/terms-of-service"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/terms-of-service');
                    }}
                    className="text-green-600 hover:text-green-700 font-medium"
                  >
                    Ketentuan Layanan
                  </a>
                  {' '}dan{' '}
                  <a
                    href="/privacy-policy"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/privacy-policy');
                    }}
                    className="text-green-600 hover:text-green-700 font-medium"
                  >
                    Kebijakan Privasi
                  </a>
                  {' '}kami.
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || isSubmitting}
                aria-busy={isLoading || isSubmitting}
                className="w-full text-white font-bold py-4 px-6 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md relative"
                style={{
                  background: 'linear-gradient(to right, #46B654, #4B63A9)',
                }}
              >
                {isLoading || isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Memproses...
                  </span>
                ) : (
                  isLoginMode ? 'Masuk' : 'Daftar'
                )}
              </button>
            </form>

            {/* Toggle Mode */}
            <div className="mt-6 text-center">
              <span className="text-gray-600 dark:text-gray-400 text-sm">
                {isLoginMode ? 'Belum punya akun? ' : 'Sudah punya akun? '}
                <button
                  type="button"
                  onClick={() => {
                    setIsLoginMode(!isLoginMode);
                    setError('');
                    setSuccess('');
                    setFieldErrors({
                      name: '',
                      email: '',
                      password: '',
                      confirmPassword: '',
                      phone: '',
                    });
                    setFormData({
                      name: '',
                      email: '',
                      password: '',
                      confirmPassword: '',
                      phone: '',
                    });
                    setAcceptedTerms(false);
                    setNetworkError(false);
                  }}
                  className="text-green-600 hover:text-green-700 font-medium text-sm underline"
                >
                  {isLoginMode ? 'Daftar di sini' : 'Masuk di sini'}
                </button>
              </span>
            </div>
          </div>
        </div>

        {/* Toast Notification */}
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={() => setToast({ isVisible: false, message: '', type: 'info' })}
        />
      </div>
    );
  }

  // Main Welcome Screen - Mobile First Design responsive untuk segala jenis mobile device
  return (
    <div className="h-screen w-screen bg-gray-50 flex flex-col items-center justify-center px-4 sm:px-6 md:px-8 relative overflow-hidden" style={{ overflow: 'hidden', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      {/* White Card Container - Mobile First responsive dengan rounded bottom corners */}
      <div className="bg-white w-full max-w-md rounded-t-3xl rounded-b-3xl flex flex-col items-center pt-8 sm:pt-10 pb-10 sm:pb-12 px-4 sm:px-6 md:px-8 relative z-10">
        {/* Logo - Fokus utama, lebih besar dengan spacing yang lebih baik */}
        <div className="text-center mb-6 sm:mb-8">
          <MosqifyLogo />
        </div>

        {/* Tagline - Di bawah logo, gray dan lebih kecil */}
        <p className="text-center text-gray-600 dark:text-gray-400 mb-8 sm:mb-10" style={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '16px', lineHeight: '24px', letterSpacing: '0.01em' }}>
          Dekat dengan Allah melalui
          <br />
          teknologi
        </p>

        {/* Action Buttons - Side by Side, Horizontal di semua ukuran layar */}
        <div className="w-full flex flex-row gap-[10px] mb-6 sm:mb-8">
          {/* Masuk - Linear Gradient #46B654 to #4B63A9 dengan shadow dan pill shape */}
          <button
            onClick={handleShowLogin}
            className="flex-1 text-white font-semibold text-sm active:opacity-90 transition-opacity"
            style={{
              background: 'linear-gradient(to right, #46B654, #4B63A9)',
              borderRadius: '48px',
              paddingTop: '16px',
              paddingBottom: '16px',
              paddingLeft: '32px',
              paddingRight: '32px',
              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            Masuk
          </button>

          {/* Guest - Border dengan Linear Gradient #46B654 to #4B63A9, pill shape */}
          <button
            onClick={handleGuestLogin}
            className="flex-1 bg-white dark:bg-gray-700 text-black dark:text-gray-200 font-semibold text-sm active:bg-gray-50 dark:active:bg-gray-600 transition-colors relative"
            style={{
              borderRadius: '48px',
              paddingTop: '15px',
              paddingBottom: '15px',
              paddingLeft: '31px',
              paddingRight: '31px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(var(--color-bg, white), var(--color-bg, white)) padding-box, linear-gradient(to right, #46B654, #4B63A9) border-box',
              border: '1px solid transparent',
              boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.1)'
            }}
          >
            Guest
          </button>
        </div>

        {/* Register Link */}
        <div className="text-center">
          <span className="text-black dark:text-gray-200 text-sm">Belum punya akun? </span>
          <button
            onClick={handleCreateAccount}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium active:opacity-80"
          >
            Daftar
          </button>
        </div>
      </div>

    </div>
  );
};

export default MosqifyApiLoginScreen;

